import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { DirectPermissionsList, LayoutComponent } from '@uxdf/direct-shared-lib';
import { DashboardPageComponent } from './components/dashboard/dashboard-page/dashboard-page.component';
import { DashboardAVCComponent } from './components/dashboard/dashboard-avc/dashboard-avc.component';
import { HomepageComponent } from './components/homepage/homepage/homepage.component';
import { ExceptionsComponent } from './components/homepage/exceptions/exceptions.component';

import { MaintenanceComponent } from './components/homepage/exceptions/maintenance/maintenance.component';

import { InvoicesPageComponent } from './components/account/invoices/invoices-page/invoices-page.component';
import { DocumentsComponent } from './components/account/invoices/documents/documents.component';
import { SegmentsComponent } from './components/account/invoices/segments/segments.component';

import { ContractsDocumentsComponent } from './components/account/contracts/contracts-documents/contracts-documents.component';

import { AccountPageComponent } from './components/account/account-page/account-page.component';
import { ProfilePageComponent } from './components/account/profile-page/profile-page.component';
import { AgencyPageComponent } from './components/account/agency-page/agency-page.component';

import { InventoryPageComponent } from './components/account/inventory-page/inventory-page.component';
import { LoyaltyProgramEnrollmentComponent } from './components/loyalty/loyalty-program-enrollment/loyalty-program-enrollment.component';
import { LoyaltyBonusPointsComponent } from './components/loyalty/loyalty-bonus-points/loyalty-bonus-points.component';
import { LoyaltyAgentsParticipationComponent } from './components/loyalty/loyalty-agents-participation/loyalty-agents-participation.component';
import { LoyaltyFinishFormComponent } from './components/loyalty/loyalty-program-enrollment/loyalty-finish-form/loyalty-finish-form.component';

import { CustomerConfigGuard } from './guards/customer-config.guard.service';
import { RegistrationGuard } from './guards/registration.guard';
import { AgreementGuard } from './guards/agreement.guard';
import { AccessGuard } from './guards/access.guard';
import { GuestGuard } from './guards/guest.guard';
import { TokenPermissionGuard } from './guards/token-permission.guard';
import { AuthGuard } from './guards/auth.guard';
import { IsDecisionMakerGuard } from './guards/IsDecisionMakerGuard.service';
import { AvailableGuard } from './guards/available.guard';

import { LogoutComponent } from './components/logout/logout.component';

import { BookPageComponent } from './components/book/book-page.component';
import { BookOrderComponent } from './components/book/book-order/book-order.component';

import { CustomPreloadingStrategyService } from './guards/preload.guard';
import { BookServiceComponent } from './components/book/book-service/book-service.component';
import { PermissionsConfigGuard } from './guards/permissions-config.guard.service';
import { PermissionConfigList } from './redux/services/utils/permission-helper.service';
import { LoyaltyStatusPointsComponent } from './components/loyalty/loyalty-status-points/loyalty-status-points.component';
import { IsFinancialManagerGuard } from './guards/IsFinancialManagerGuard.service';
import { AvcInvoicesComponent } from './components/account/avc-invoices/components/avc-invoices.component';
import { AvcCreditLineComponent } from './components/avc-credit-line/components/avc-credit-line/avc-credit-line.component';
import { AdHomePageReloadFromAshGuard } from './guards/AdHomePageReloadFromAshGuard';
import { ContractsPageComponent } from './components/account/contracts/contracts-page/contracts-page.component';

/* eslint-disable */
const um_title = 'User Management | Amadeus Direct';
type PathMatch = 'full' | 'prefix' | undefined;

const routes: Routes = [
  {
    path: 'login',
    component: LayoutComponent,
    canActivate: [GuestGuard],
    data: { title: 'Amadeus Direct - exclusive community portal for SME travel agencies' }
  },
  {
    path: 'registration',
    loadChildren: () => import('./modules/registration/registration.module').then(m => m.RegistrationModule),
    canActivate: [RegistrationGuard, GuestGuard],
    data: { title: 'Registration | Amadeus Direct', preload: true }
  },
  {
    path: 'agreement',
    loadChildren: () => import('./modules/agreement/agreement.module').then(m => m.AgreementModule),
    canActivate: [AgreementGuard, GuestGuard],
    data: { title: 'Agreement | Amadeus Direct', preload: true }
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full' as PathMatch,
    data: { title: 'Homepage | Amadeus Direct' }
  },
  {
    path: 'home',
    component: HomepageComponent,
    canActivate: [AuthGuard, AdHomePageReloadFromAshGuard],
    data: { title: 'Homepage | Amadeus Direct' }
  },
  {
    path: 'book',
    component: BookPageComponent,
    canActivate: [AuthGuard, TokenPermissionGuard],
    data: {
      title: 'TripMaker - Beta | Amadeus Direct',
      permissionGard: {
        requiredPermission: DirectPermissionsList.book,
        redirect: '/book/order'
      }
    },
    children: [
      {
        path: '',
        pathMatch: 'full' as PathMatch,
        component: BookServiceComponent
      },
      {
        path: 'order',
        component: BookOrderComponent,
        data: {
          title: 'TripMaker - Beta | Amadeus Direct'
        }
      }
    ]
  },
  {
    path: 'faq',
    loadChildren: () => import('./modules/faq/faq.module').then(m => m.FaqModule),
    canActivate: [AuthGuard],
    data: { title: 'FAQ | Amadeus Direct', preload: true }
  },
  {
    // path renamed 04 Feb 2020
    path: 'loyalty',
    redirectTo: 'amadeus-plus-me'
  },
  {
    path: 'amadeus-plus-me/details',
    component: LoyaltyProgramEnrollmentComponent,
    canActivate: [AuthGuard],
    data: { title: 'Amadeus+Me | Amadeus Direct', accessGuardRedirect: 'amadeus-plus-me/enrollment' }
  },
  {
    path: 'amadeus-plus-me/enrollment',
    component: LoyaltyProgramEnrollmentComponent,
    canActivate: [AuthGuard],
    data: { title: 'Amadeus+Me | Amadeus Direct', accessGuardRedirect: 'amadeus-plus-me/participation' }
  },
  {
    path: 'amadeus-plus-me/bonuspoints',
    component: LoyaltyBonusPointsComponent,
    canActivate: [AuthGuard],
    data: { title: 'Amadeus+Me | Amadeus Direct', accessGuardRedirect: 'amadeus-plus-me/enrollment' }
  },
  {
    path: 'amadeus-plus-me/statuspoints',
    component: LoyaltyStatusPointsComponent,
     canActivate: [AuthGuard],
    data: { title: 'Amadeus+Me | Amadeus Direct', accessGuardRedirect: 'amadeus-plus-me/enrollment' }
  },
  {
    path: 'amadeus-plus-me/participation',
    component: LoyaltyAgentsParticipationComponent,
    canActivate: [AuthGuard],
    data: { title: 'Amadeus+Me | Amadeus Direct', accessGuardRedirect: 'amadeus-plus-me/enrollment' }
  },
  {
    path: 'amadeus-plus-me/finish',
    component: LoyaltyFinishFormComponent,
    canActivate: [AuthGuard],
    data: {
      title: 'Amadeus+Me | Amadeus Direct',
      accessGuardCode: 'amadeus-plus-me/details',
      accessGuardRedirect: 'amadeus-plus-me/enrollment'
    }
  },
  {
    path: 'content',
    loadChildren: () => import('./modules/content/content.module').then(m => m.ContentModule),
    data: { preload: true }
  },
  {
    path: 'contact',
    loadChildren: () => import('./modules/contact/contact.module').then(m => m.ContactModule),
    canActivate: [AuthGuard],
    data: { title: 'Contact Us | Amadeus Direct', preload: true }
  },
  {
    path: 'dashboard',
    component: DashboardPageComponent,
    canActivate: [AuthGuard],
    data: {
      title: 'My Dashboard | Amadeus Direct',
      permission: PermissionConfigList.CONFIG_DASHBOARD_ENABLED
    }
  },
  {
    path: 'avc-dashboard',
    component: DashboardAVCComponent,
    canActivate: [AuthGuard, CustomerConfigGuard, PermissionsConfigGuard],
    data: {
      title: 'My AVC Dashboard | Amadeus Direct',
      permission: PermissionConfigList.CONFIG_AVC_DASHBOARD_ENABLED
    }
  },
  {
    path: 'avc-creditline',
    component: AvcCreditLineComponent,
    canActivate: [AuthGuard, CustomerConfigGuard, AccessGuard],
    data: {
      title: 'Avc credit line | Amadeus Direct'
    }
  },
  {
    path: 'logout',
    component: LogoutComponent
  },
  {
    path: 'news',
    loadChildren: () => import('./modules/news/news.module').then(m => m.NewsModule),
    canActivate: [AuthGuard, CustomerConfigGuard, PermissionsConfigGuard],
    data: {
      title: 'News | Amadeus Direct',
      preload: true,
      permission: PermissionConfigList.CONFIG_NEWS_ENABLED
    }
  },
  {
    path: '403',
    component: ExceptionsComponent,
    data: { code: 403, title: '403 | Amadeus Direct' }
  },
  {
    path: '404',
    component: ExceptionsComponent,
    data: { code: 404, title: '404 | Amadeus Direct' }
  },
  {
    path: '500',
    component: ExceptionsComponent,
    data: { code: 500, title: '500 | Amadeus Direct' }
  },
  {
    path: 'maintenance',
    canActivate: [AvailableGuard],
    component: MaintenanceComponent
  },

  {
    path: 'account',
    component: AccountPageComponent,
    data: {
      title: 'accountMyAccount'
    },
    children: [
      {
        path: 'profile',
        component: ProfilePageComponent,
        canActivate: [AuthGuard, CustomerConfigGuard],
        data: {
          title: 'Profile | Amadeus Direct',
          titleBarFirstLine: $localize`:@@accountProfile.profileHeading:Profile`
        }
      },
      {
        path: 'agency',
        component: AgencyPageComponent,
        canActivate: [AuthGuard, CustomerConfigGuard],
        data: {
          title: 'My Agency Details | Amadeus Direct',
          titleBarFirstLine: $localize`:@@accountProfile.agencyTitle:Agency details`
        }
      },
      {
        path: 'invoices',
        component: InvoicesPageComponent,
        canActivate: [AuthGuard],
        data: {
          title: 'Invoices | Amadeus Direct',
          titleBarFirstLine: $localize`:@@accountProfile.accountInvoices:Invoices`
        },
        children: [
          {
            path: 'documents',
            component: DocumentsComponent,
            canActivate: [IsFinancialManagerGuard]
          },
          {
            path: 'segments',
            component: SegmentsComponent,
            canActivate: [IsFinancialManagerGuard]
          }
        ]
      },
      {
        path: 'avc-invoices',
        component: AvcInvoicesComponent,
        canActivate: [AuthGuard, CustomerConfigGuard, AccessGuard],
        data: {
          title: 'Value Hotels invoices | Amadeus Direct',
          titleBarFirstLine: $localize`:@@accountProfile.accountAvcInvoices:Value Hotels invoices`
        }
      },
      {
        path: 'contracts',
        component: ContractsPageComponent,
        canActivate: [AuthGuard, CustomerConfigGuard, AccessGuard],
        data: {
          title: 'Contracts | Amadeus Direct',
          titleBarFirstLine: $localize`:@@accountProfile.accountContracts:Contracts`
        },
        children: [
          {
              path: 'documents',
              component: ContractsDocumentsComponent,
              canActivate: [IsDecisionMakerGuard]
          }
        ]
      },
      {
        path: 'inventory',
        component: InventoryPageComponent,
        canActivate: [AuthGuard, CustomerConfigGuard, AccessGuard],
        data: {
          title: 'Inventory | Amadeus Direct',
          titleBarFirstLine: $localize`:@@accountProfile.accountInventoryMyInventory:Inventory`,
          titleBarSecondLine: $localize`:@@accountProfile.accountInventoryTitle:You can find the products and services that you currently own here`
        }
      }
    ]
  },
  {
    path: '**',
    redirectTo: '404'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
